import React, { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import { api, formatMXN } from "../lib/api";
import { Wallet, Landmark, CreditCard, Inbox } from "lucide-react";
import TransactionCard from "./cards/TransactionCard";
import TransactionDetailSheet from "./TransactionDetailSheet";
import EmptyState from "./EmptyState";
import { useScope } from "../context/ScopeContext";

const ACCOUNT_ICONS = { efectivo: Wallet, banco: Landmark, tarjeta: CreditCard };

export default function AccountDetailSheet({ account, onClose }) {
  const { scope } = useScope();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!account) return;
    setLoading(true);
    api.get("/transactions", { params: { scope } })
      .then(({ data }) => {
        setTxs(data.filter((t) => t.account_id === account.id));
      })
      .catch(() => setTxs([]))
      .finally(() => setLoading(false));
  }, [account, scope]);

  if (!account) return null;
  const Icon = ACCOUNT_ICONS[account.type] || Wallet;
  const negative = (Number(account.balance) || 0) < 0;

  return (
    <>
      <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <SheetContent
          side="bottom"
          data-testid="account-detail-sheet"
          className="bg-bone border-line rounded-t-3xl p-0 max-w-md mx-auto overflow-y-auto overscroll-contain"
          style={{ maxHeight: "92dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 1.5rem)" }}
        >
          <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-line/80" aria-hidden />
          <SheetHeader className="text-left px-6 pt-1">
            <SheetTitle className="sr-only">Historial de {account.name}</SheetTitle>
          </SheetHeader>

          {/* Header ficha */}
          <div className="px-6 pt-2 pb-4">
            <div className="bg-white border border-line/70 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center shrink-0">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-soft">
                    {account.type}
                  </p>
                  <p className="text-lg font-bold text-ink truncate" data-testid="account-detail-name">
                    {account.name}
                  </p>
                </div>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-soft mt-3">
                Saldo actual
              </p>
              <p
                className={`font-display text-3xl font-extrabold tabular ${negative ? "text-clay" : "text-ink"}`}
                data-testid="account-detail-balance"
              >
                {formatMXN(account.balance)}
              </p>
            </div>
          </div>

          {/* Historial */}
          <div className="px-6 pb-4">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-sage-700 mb-2">
              Historial · {txs.length} movimiento{txs.length === 1 ? "" : "s"}
            </h3>
            <div className="bg-white border border-line/70 rounded-2xl px-4">
              {loading ? (
                <div className="py-6 text-center text-soft text-sm">Cargando…</div>
              ) : txs.length === 0 ? (
                <EmptyState
                  icon={Inbox}
                  title="Sin movimientos"
                  description="Aún no hay ingresos ni gastos aplicados a esta cuenta."
                  testid="account-detail-empty"
                />
              ) : (
                txs.map((t) => (
                  <TransactionCard
                    key={t.id}
                    tx={t}
                    testid={`account-detail-tx-${t.id}`}
                    onClick={() => setDetail(t)}
                  />
                ))
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <TransactionDetailSheet tx={detail} onClose={() => setDetail(null)} />
    </>
  );
}
